import React, { Component, Fragment } from 'react';
import { arrayOf, bool, string, func, shape, node } from 'prop-types';
import Link from 'components/Link';
import BundleSponsor from 'components/BundleSponsor';
import EmailSignupConfirmation from '../EmailSignupFormConfirmation';

/**
 * displays signup form, manages its validation & submission, and displays the
 * confirmation message or error.
 *
 */
class EmailSignupForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      formData: {
        email: ''
      },
      hasSubscribed: false,
      hasError: false,
      isPending: false
    };

    this._handleInputChange = this.handleInputChange.bind(this);
    this._handleFormSubmit = this.handleFormSubmit.bind(this);
  }

  handleFormSubmit(e) {
    e.preventDefault();
    const {
      formData: { email }
    } = this.state;
    const {
      subscribeUserToList,
      listKey,
      optInEvent,
      onSubscribe
    } = this.props;
    this.setState(
      {
        isPending: true
      },
      () => {
        // the email subs API returns a 200 with no data to signify success. if
        // there are any subscription errors, then it returns a 400.
        subscribeUserToList({
          email,
          listKey,
          optInEvent
        }).then(
          // resolve promise
          () =>
            this.setState({ hasSubscribed: true, isPending: false }, () => {
              if (onSubscribe) {
                onSubscribe(this.state.formData);
              }
            }),
          // reject promise (HTTP error)
          error => {
            this.setState({ hasError: true, isPending: false });
            // CLEANUP: catch/log error
            console.error(error); // eslint-disable-line
          }
        );
      }
    );
  }

  handleInputChange(e) {
    this.setState({
      formData: { ...this.state.formData, email: e.currentTarget.value }
    });
  }

  render() {
    const {
      formData: { email },
      hasSubscribed,
      hasError,
      isPending
    } = this.state;
    const {
      title,
      description,
      label,
      customConfirmationHtml,
      socialLinks,
      sponsor,
      hasFocus
    } = this.props;

    if (hasSubscribed === false) {
      return (
        <Fragment>
          <section className="EmailSignup__body">
            {title && <h2 className="EmailSignup__title">{title}</h2>}
            {description && <p className="EmailSignup__desc">{description}</p>}
            {sponsor && <BundleSponsor cssHook="EmailSignup" {...sponsor} />}
          </section>

          <form className="EmailSignup__form" onSubmit={this._handleFormSubmit}>
            {isPending ? (
              <p className="EmailSignup__pending">Subscribing...</p>
            ) : (
              <label
                className="EmailSignup__label"
                htmlFor="email-signup-email"
              >
                <div className="EmailSignup__label-placeholder">{label}</div>
                <div className="EmailSignup__input">
                  <input
                    className="input--block"
                    id="email-signup-email"
                    type="email"
                    name="email"
                    value={email}
                    onChange={this._handleInputChange}
                    aria-label="sign up email"
                    required
                    autoFocus={hasFocus} // eslint-disable-line
                  />
                </div>
                <input
                  type="submit"
                  className="EmailSignup__button"
                  aria-label="submit email"
                  value="Sign up"
                />
              </label>
            )}
            {hasError && (
              <div className="EmailSignup__errors">
                <div className="EmailSignup__error">
                  Sorry, there seems to be an issue submitting your subscription
                  request. Try again later.
                </div>
              </div>
            )}
            {/* prettier-ignore */}
            <div className="EmailSignup__legal">
              <Link as="/about/terms" href="/page?path=/about/terms"><a>Terms of Use</a></Link> and <Link as="/about/privacy-policy" href="/page?path=/about/privacy-policy"><a>Privacy Policy</a></Link>
            </div>
          </form>
        </Fragment>
      );
    }

    return (
      <EmailSignupConfirmation
        socialLinks={socialLinks}
        confirmationMsgHtml={customConfirmationHtml}
      />
    );
  }
}

EmailSignupForm.propTypes = {
  description: string,
  hasFocus: bool,
  label: string,
  listKey: string.isRequired,
  socialLinks: arrayOf(
    shape({
      url: string,
      type: string
    })
  ).isRequired,
  optInEvent: string,
  sponsor: shape({
    name: string,
    messaging: string,
    imageUrl: string
  }),
  subscribeUserToList: func,
  title: string,
  customConfirmationHtml: node,
  onSubscribe: func
};

EmailSignupForm.defaultProps = {
  description: undefined,
  hasFocus: false,
  label: 'email address',
  optInEvent: '',
  sponsor: {},
  subscribeUserToList: () => Promise.resolve({}),
  title: undefined,
  customConfirmationHtml: undefined,
  onSubscribe: undefined
};

export default EmailSignupForm;
